import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import Dropdown from "../../../../components/Dropdown";
import GridTable from "../../../../components/GridTable";
import ActionMenu from "../../../../components/ActionMenu";
import Dialog from "../../../../components/Dialog";
import Loader from "../../../../components/Loader";
import Button from "../../../../components/Button";
import { successMessage, errorMessage } from "../../../../components/Toaster";
import {
  getAllMachines,
  getCompanyYardMachines,
  attachMachine,
  detachMachine,
} from "./api";
import { getErrorMessage } from "../../../../utils/error";

const Machine = () => {
  const location = useLocation();
  const { company_id, yard_id } = useParams();
  const company_name = location.state?.company_name || "Company";
  const yard_name = location.state?.yard_name || "Yard";
  const [companyMachines, setCompanyMachines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState({
    fetch: false,
    create: false,
    delete: false,
  });

  const validationSchema = Yup.object().shape({
    machineId: Yup.string().required("Machine is required"),
  });

  const columns = [
    {
      field: "machine_name",
      headerName: "Machine Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: () => handleDelete(params),
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  useEffect(() => {
    const fetchMachines = async () => {
      setLoading((prev) => ({ ...prev, fetch: true }));
      try {
        const params = {
          company_id: company_id || "",
          yard_id: yard_id || "",
        };

        const res = await getCompanyYardMachines(params);

        if (res.status && res.data) {
          setCompanyMachines(res.data);
        } else {
          errorMessage(res.message || "Failed to fetch machines.");
        }
      } catch (error) {
        errorMessage(getErrorMessage(error));
      } finally {
        setLoading((prev) => ({ ...prev, fetch: false }));
      }
    };

    fetchMachines();
  }, [company_id, yard_id]);

  const openDialog = async () => {
    setDialogOpen(true);
    setLoading((prev) => ({ ...prev, fetch: true }));

    try {
      const res = await getAllMachines();
      if (res.status && res.data) {
        setMachines(res.data);
      } else {
        errorMessage(res.message || "Failed to fetch machines.");
      }
    } catch (error) {
      errorMessage(getErrorMessage(error));
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const confirmDialog = async (values, { resetForm }) => {
    const success = await handleCreate(values);
    if (success) {
      closeDialog(resetForm);
    }
  };

  const handleCreate = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const newMachine = {
        machine_id: values.machineId,
        company_id: company_id,
        yard_id: yard_id,
      };
      const response = await attachMachine(newMachine);
      if (response.status && response.data) {
        const machineData = machines.find(
          (m) => m.machine_id === values.machineId,
        );
        const machine = {
          company_machine_id: response.data.company_machine_id,
          machine_id: values.machineId,
          machine_name: machineData?.machine_name || "",
        };

        setCompanyMachines((prev) => [...prev, machine]);
        successMessage(response.message);
        return true;
      } else {
        errorMessage(response.message || "Failed to attach machine.");
        return false;
      }
    } catch (error) {
      errorMessage(getErrorMessage(error));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));

    try {
      if (itemToDelete) {
        const response = await detachMachine(
          itemToDelete.row.company_machine_id,
        );

        if (response.status) {
          setCompanyMachines((prev) =>
            prev.filter(
              (m) =>
                m.company_machine_id !== itemToDelete.row.company_machine_id,
            ),
          );
          successMessage(response.message);
        } else {
          errorMessage(response.message || "Failed to delete machine.");
        }
      }
    } catch (error) {
      errorMessage(getErrorMessage(error));
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setDeleteOpen(false);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                <Link color="primary" to="/company">
                  Company
                </Link>
                <Link color="primary" to={`/company`}>
                  {company_name}
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name: company_name, yard_name: yard_name }}>
                  Yard
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name: company_name, yard_name: yard_name }}>
                  {yard_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/yards/${yard_id}/machines`}
                  state={{ company_name: company_name, yard_name: yard_name }}
                >
                  Machines
                </Link>
              </Breadcrumbs>
            </div>

            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  label="Add"
                  variant="contained"
                  color="primary"
                  onClick={openDialog}
                />
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-20"
            >
              <GridTable
                rows={companyMachines}
                columns={columns}
                pageSize={10}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.company_machine_id}
                density="standard"
              />
            </div>
          </main>
        </Box>
      )}
      <Formik
        initialValues={{ machineId: "" }}
        validationSchema={validationSchema}
        onSubmit={confirmDialog}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => (
          <Dialog
            open={isDialogOpen}
            onClose={() => closeDialog(resetForm)}
            onConfirm={handleSubmit}
            title="Attach New Machine"
            confirmButtonText="Attach"
            cancelButtonText="Cancel"
            icon={<MiscellaneousServicesIcon />}
            message={
              <Box padding={4}>
                {loading.create ? (
                  <Loader color="primary" size={40} />
                ) : (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Dropdown
                          options={machines.map((machine) => ({
                            id: machine.machine_id,
                            name: `${machine.machine_name}`,
                          }))}
                          value={values.machineId}
                          onChange={(id) => setFieldValue("machineId", id)}
                          label="Machine"
                          required
                          error={touched.machineId && Boolean(errors.machineId)}
                          helperText={touched.machineId && errors.machineId}
                        />
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Box>
            }
          />
        )}
      </Formik>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={!loading.delete ? "Detach Machine" : ""}
        confirmButtonText={!loading.delete ? "Confirm" : ""}
        cancelButtonText={!loading.delete ? "Cancel" : ""}
        icon={<DeleteIcon />}
        message={
          <Box padding={2}>
            {loading.delete ? (
              <Loader color="primary" size={40} />
            ) : (
              "Are you sure you want to detach this machine?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default Machine;
